import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DataTable from "examples/Tables/DataTable";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { ToastContainer, toast } from "react-toastify";
import { Stack, CircularProgress } from "@mui/material";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import SearchIcon from "@mui/icons-material/Search";
import Papa from "papaparse";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import MDBadge from "components/MDBadge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import colors from "assets/theme/base/colors";
import "../campus/Campus.css";
import Select from "react-select";
import nodata from "assets/images/nodata.png";
import { hasAdmissionPermission } from "hooks/permissions";

const { info } = colors;
const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

const TestCandidateReport = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const userDepartment = JSON.parse(sessionStorage.getItem("department_id"));
  const [loading, setLoading] = useState(false);
  const [loadingTests, setLoadingTests] = useState(false);

  // Session Storage
  const user = JSON.parse(sessionStorage.getItem("user"));
  const organization_id = user ? user.organization_id : null;

  // Variable Declarations
  const [allCandidates, setAllCandidates] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [campaignIds, setCampaignIds] = useState([]);
  const [testIds, setTestIds] = useState([]);
  const [allTests, setAllTests] = useState([]);
  const [originalCandidates, setOriginalCandidates] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [paidStatusFilter, setPaidStatusFilter] = useState("");

  // Add state for selected values to control the Select components
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);

  const handleViewCandidate = (id) => {
    window.open(`/admissions/get-applications/${id}`);
  };

  const handleSearchKeywordChange = (e) => {
    const searchTerm = e.target.value.trim().toLowerCase();
    setSearchKeyword(searchTerm);
    
    if (!searchTerm) {
      setAllCandidates(originalCandidates);
      return;
    }

    const filteredCandidates = originalCandidates.filter((candidate) => {
      return (
        (candidate.id && candidate.id.toString().toLowerCase().includes(searchTerm)) ||
        (candidate.name && candidate.name.toLowerCase().includes(searchTerm)) ||
        (candidate.test_name && candidate.test_name.toLowerCase().includes(searchTerm)) ||
        (candidate.roll_number && candidate.roll_number.toLowerCase().includes(searchTerm))
      );
    });

    setAllCandidates(filteredCandidates);
  };

  const handleExportCSV = () => {
    if (allCandidates.length === 0) return;
    
    // Define the headers for the CSV
    const headers = [
      "Sr.No",
      "Application ID", 
      "Name" , 
      "Father Name",
      "Gender",
      "DOB",
      "Religion",
      "City", 
      "Province", 
      "Emergency Contact Name",
      "Emergency Contact No", 
      "Applicant CNIC", 
      "Father CNIC",
      "Nationality", 
      "Disability",  
      "Father Occupation",
      "Father Income",
      "BZU Registration No",
      "Current Address",
      "Permanent Address",
      "Telephone No",
      "Father Phone", 
      "Applicant Phone",
      "Hafiz Quran",
      "Emergency Contact Address", 
      "Need Transport",
      "Campaign Name",
      "Paid Status",
      "Programs"
    ];

    // Map the rows to include all necessary fields
    const data = allCandidates.map((candidate, index) => ({
      serial: index + 1,
      id: candidate.id,
      name: candidate.name,
      father_name: candidate.father_name,
      gender: candidate.gender,
      dob: candidate.dob,
      religion: candidate.religion,
      p_city: candidate.p_city,
      p_province: candidate.p_province,
      emergency_contact_name: candidate.emergency_contact_name,
      emergency_contact_no: candidate.emergency_contact_no,
      applicant_cnic: candidate.applicant_cnic,
      father_cnic: candidate.father_cnic,
      nationality: candidate.nationality,
      disability: candidate.disability,
      father_occupation: candidate.father_occupation,
      father_income: candidate.father_income,
      bzu_registration_no: candidate.bzu_registration_no,
      current_address: candidate.current_address,
      permanent_address: candidate.permanent_address,
      tel_no: candidate.tel_no,
      father_phone: candidate.father_phone,
      applicant_phone: candidate.applicant_phone,
      hafiz_quran: candidate.hafiz_quran,
      emergency_contact_address: candidate.emergency_contact_address,
      need_transport: candidate.need_transport,
      campaign_name: candidate.campaign_name,
      paid_status: candidate.paid_status == 1 ? "Paid" : "Unpaid",
      programs: candidate.programs
    }));

    // Convert to CSV format
    const csv = Papa.unparse([headers, ...data.map((row) => Object.values(row))]);

    // Create a blob and download the CSV
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = "TestCandidatesApplications.csv";
    link.click();
  };

  // API calls
  const fetchAllCampaigns = async () => {
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await fetch(BASE_URL + `/campaign/get_campaigns`, requestOptions);
      const data = await response.json();
      setAllCampaigns(data.campaigns);
      
      // Auto-select first active campaign if userDepartment exists
      if (userDepartment && data.campaigns && data.campaigns.length > 0) {
        const activeCampaigns = data.campaigns.filter(camp => camp.status === 1);
        if (activeCampaigns.length > 0) {
          const firstActiveCampaign = activeCampaigns[0];
          const newCampaignIds = [firstActiveCampaign.id];
          const newSelectedCampaigns = [{
            value: firstActiveCampaign.id,
            label: firstActiveCampaign.name
          }];
          
          setCampaignIds(newCampaignIds);
          setSelectedCampaigns(newSelectedCampaigns);
          
          // Fetch tests for the auto-selected campaign
          fetchTestsByCampaigns(newCampaignIds);
        }
      }
    } catch (error) {
      console.log("Error fetching campaigns:", error);
    }
  };

  const fetchTestsByCampaigns = async (campaignIdsArray) => {
    if (!campaignIdsArray || campaignIdsArray.length === 0) {
      setAllTests([]);
      setSelectedTests([]);
      setTestIds([]);
      return;
    }

    setLoadingTests(true);
    
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          campaign_ids: campaignIdsArray
        }),
      };

      const response = await fetch(
        BASE_URL + `/entrytest/get_entry_test_by_campaigns`,
        requestOptions
      );
      const result = await response.json();
      
      if (response.ok) {
        const tests = result.entry_tests || [];
        setAllTests(tests);
        
        // Clear selected tests when campaigns change
        setSelectedTests([]);
        setTestIds([]);
      } else {
        toast.error(result.error || "Failed to fetch tests");
        setAllTests([]);
        setSelectedTests([]);
        setTestIds([]);
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("An error occurred while fetching tests");
      setAllTests([]);
      setSelectedTests([]);
      setTestIds([]);
    } finally {
      setLoadingTests(false);
    }
  };

  const getFilteredTestCandidates = async () => {
    // Check if at least one campaign is selected
    if (!campaignIds || campaignIds.length === 0) {
      toast.error("Please select at least one campaign");
      return;
    }

    // Check if at least one test is selected
    if (!testIds || testIds.length === 0) {
      toast.error("Please select at least one test");
      return;
    }

    setLoading(true);
    
    // Prepare filter data - now sending test IDs instead of test names
    const filterData = {
      campaign_ids: campaignIds,
      test_ids: testIds, // Changed from test_names to test_ids
      paid_status: paidStatusFilter || null
    };

    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(filterData),
      };

      const response = await fetch(
        BASE_URL + `/application/get_filtered_test_candidates`,
        requestOptions
      );
      const result = await response.json();
      
      if (response.ok) {
        const candidates = result.candidates || [];
        setAllCandidates(candidates);
        setOriginalCandidates(candidates);
        
        toast.success(`${result.total_count || candidates.length} test candidates found`);
      } else {
        toast.error(result.error || "Failed to fetch test candidates");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("An error occurred while fetching test candidates");
    } finally {
      setLoading(false);
    }
  };

  // Handle campaign selection change
  const handleCampaignChange = (selectedOptions) => {
    const newCampaignIds = selectedOptions ? selectedOptions.map(item => item.value) : [];
    setCampaignIds(newCampaignIds);
    setSelectedCampaigns(selectedOptions || []);
    
    // Fetch tests for the newly selected campaigns
    fetchTestsByCampaigns(newCampaignIds);
  };

  // Handle test selection change
  const handleTestChange = (selectedOptions) => {
    const newTestIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setTestIds(newTestIds);
    setSelectedTests(selectedOptions || []);
  };

  // Table Configuration
  const columns = [
    { Header: "Sr.No", accessor: "serial", align: "center" },
    { Header: "Application ID", accessor: "id", align: "center" },
    { Header: "Applicant Name", accessor: "applicant_name", align: "center" },
    { Header: "Father Name", accessor: "father_name", align: "center" },
    { Header: "Applicant Phone", accessor: "applicant_phone", align: "center" },
    { Header: "Paid Status", accessor: "paid_status", align: "center" },
    { Header: "Programs", accessor: "programs", align: "left" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Actions", accessor: "actions", align: "center" },
  ];

  const rows = allCandidates.length > 0
    ? allCandidates.map((candidate, index) => ({
        serial: index + 1,
        id: candidate.id,
        applicant_name: candidate.name,
        father_name: candidate.father_name,
        applicant_phone: candidate.applicant_phone,
        status: candidate.is_completed === 1 ? (
          <MDBox ml={-1}>
            <MDBadge
              badgeContent="Completed"
              color="success"
              variant="gradient"
              size="sm"
            />
          </MDBox>
        ) : (
          <MDBox ml={-1}>
            <MDBadge
              badgeContent="Incomplete"
              color="warning"
              variant="gradient"
              size="sm"
            />
          </MDBox>
        ),
        paid_status: candidate.paid_status === 1 ? "Paid" : "Unpaid",
        test_name: candidate.test_name,
        programs: candidate.programs,
        actions: (
          <>
          {hasAdmissionPermission("TestCandidateReport", "READ") && (
            <IconButton onClick={() => handleViewCandidate(candidate.id)}>
              <VisibilityIcon style={{ color: "blue" }} />
            </IconButton>
          )}
          </>
        ),
      }))
    : [
        {
          serial: null,
          id: null,
          applicant_name: null,
          father_name: null,
          dob: null,
          applicant_phone: null,
          gender: null,
          province: null,
          status: null,
          paid_status: null,
          test_name: null,
          actions: null,
        },
      ];

  // Initial data loading
  useEffect(() => {
    // Load initial data
    fetchAllCampaigns();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <MDBox pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className="mb-4">
              <MDBox p={3}>
                <MDTypography variant="h5" fontWeight="medium" mb={2}>
                  Test Candidate Filters
                </MDTypography>
                <Grid container spacing={2}>
                  {/* Campaign Filter */}
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="subtitle2" fontWeight="light" mb={1}>
                      Campaigns
                    </MDTypography>
                    <Select
                      isMulti
                      name="campaign_ids"
                      options={allCampaigns.map((camp) => ({
                        value: camp.id,
                        label: camp.name,
                      }))}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      placeholder="Select Campaigns"
                      value={selectedCampaigns}
                      onChange={handleCampaignChange}
                    />
                  </Grid>

                  {/* Test Filter */}
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="subtitle2" fontWeight="light" mb={1}>
                      Tests
                    </MDTypography>
                    <Select
                      isMulti
                      options={allTests.map((test) => ({
                        value: test.id,
                        label: test.title,
                      }))}
                      isLoading={loadingTests}
                      isClearable
                      isSearchable
                      name="test_ids"
                      className="basic-multi-select"
                      classNamePrefix="select"
                      placeholder={loadingTests ? "Loading tests..." : "Select Tests"}
                      value={selectedTests}
                      onChange={handleTestChange}
                      isDisabled={loadingTests || allTests.length === 0}
                    />
                  </Grid>

                  {/* Paid Status Filter */}
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="subtitle2" fontWeight="light" mb={1}>
                      Paid Status
                    </MDTypography>
                    <Select
                      name="paid_status"
                      options={[
                        { value: "1", label: "Paid" },
                        { value: "0", label: "Unpaid" },
                      ]}
                      className="basic-select"
                      classNamePrefix="select"
                      placeholder="Select Paid Status"
                      onChange={(selected) => {
                        setPaidStatusFilter(selected?.value || "");
                      }}
                      isClearable
                    />
                  </Grid>

                  {/* Action Buttons */}
                  <Grid item xs={12} display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={getFilteredTestCandidates}
                      disabled={loading}
                      sx={{ mt: 2 }}
                      style={{color: "white"}}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Fetch Test Candidates"
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>

            <Card>
              <MDBox
                mx={0}
                mt={0}
                py={2}
                px={2}
                variant="gradient"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  sx={{ width: "100%" }}
                >
                  <MDTypography variant="h6" fontWeight="medium">
                    Test Candidates Report
                  </MDTypography>

                  <Stack direction="row" spacing={2}>
                    <Paper component="form" className="search-paper">
                      <InputBase
                        sx={{ ml: 1, flex: 1, fontSize: "16px" }}
                        placeholder="Search"
                        inputProps={{ "aria-label": "search test candidates" }}
                        value={searchKeyword}
                        onChange={handleSearchKeywordChange}
                      />
                      <SearchIcon className="search-icon" />
                    </Paper>
                  {hasAdmissionPermission("TestCandidateReport", "EXPORT CSV") && (
                    <Button
                      variant="contained"
                      color="primary"
                      endIcon={<DocumentScannerIcon />}
                      onClick={handleExportCSV}
                      style={{color: "white"}}
                    >
                      Export CSV
                    </Button>
                  )}
                  </Stack>
                </Stack>
              </MDBox>
              
              <MDBox pt={3} pb={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress />
                  </MDBox>
                ) : allCandidates && allCandidates.length > 0 ? (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                ) : (
                  <MDBox display="flex" flexDirection="column" alignItems="center" p={5}>
                    <img
                      src={nodata}
                      style={{
                        width: "20%",
                        height: "20%",
                      }}
                      alt="No Data"
                    />
                    <MDTypography variant="body1" mt={2}>
                      No test candidates found. Please adjust your filters and try again.
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default TestCandidateReport;